import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, RefreshCw, FileDown, Sparkles, Copy } from "lucide-react";
import { api, money, shortDate } from "../lib/api";
import { apiError } from "../lib/auth";
import { PageHeader, NeonButton } from "../components/common";
import { Insights } from "../components/admin/Insights";
import { ErrorCenter } from "../components/admin/ErrorCenter";
import { SecurityBody } from "./Security";
import { GuardConsole } from "../components/guard/GuardConsole";
import { useGuard } from "../components/guard/GuardContext";

const ago = (iso) => { if (!iso) return "never"; const m = Math.round((Date.now() - new Date(iso)) / 60000); return m < 1 ? "just now" : m < 60 ? `${m} min ago` : m < 1440 ? `${Math.round(m / 60)} h ago` : `${Math.round(m / 1440)} d ago`; };
const Dot = ({ ok }) => (ok ? <CheckCircle2 className="w-4 h-4 text-brand" /> : <XCircle className="w-4 h-4 text-red-400" />);
const Box = ({ title, children, testid, className = "" }) => (
  <section className={`panel p-5 ${className}`} data-testid={testid}><p className="label-xs mb-3">{title}</p>{children}</section>
);

const Health = ({ h }) => (
  <Box title="System health" testid="monitor-health">
    <div className="space-y-2 text-sm">
      {[["API", h.api], ["Database", h.database], ["Video storage", h.storage], ["Whop webhook secret", h.whop.webhook_secret], ["Whop API key", h.whop.api_key]].map(([k, v]) => (
        <p key={k} className="flex items-center gap-2" data-testid={`health-${k.toLowerCase().replace(/\s/g, "-")}`}><Dot ok={v} />{k}</p>
      ))}
      <p className="text-xs text-white/45 pt-2">Last Whop event: {h.last_whop_event ? `${h.last_whop_event.type} · ${ago(h.last_whop_event.received_at)}` : "none yet"}</p>
      <p className="text-xs text-white/45">Retention job: {h.retention_last_run ? `${ago(h.retention_last_run.at)} · ${h.retention_last_run.purged} purged` : "not run yet"}</p>
    </div>
  </Box>
);

const Pilot = ({ p }) => {
  const link = p.invite ? `${window.location.origin}/invite/${p.invite.token}` : null;
  return (
    <Box title="Pilot merchant" testid="monitor-pilot">
      <p className="font-bold">{p.name} <span className="text-white/45 font-normal text-sm">{p.email}</span></p>
      {p.user ? (
        <div className="text-sm text-white/70 mt-2 space-y-1">
          <p data-testid="pilot-last-seen">Last seen {ago(p.user.last_seen)} on <span className="font-mono text-xs">{p.user.last_page || "—"}</span> · {p.user.events_24h} actions in 24 h</p>
          <p data-testid="pilot-training">Training {p.training.completed}/{p.training.total}{p.training.next && ` · next: ${p.training.next.title}`}</p>
        </div>
      ) : link ? (
        <div className="mt-2" data-testid="pilot-invite">
          <p className="text-sm text-amber-300">Hasn't joined yet. Send him this owner invite link (expires {shortDate(p.invite.expires_at)}):</p>
          <button onClick={() => navigator.clipboard.writeText(link).then(() => toast.success("Invite link copied"))} className="mt-2 flex items-center gap-2 font-mono text-[11px] text-brand break-all text-left" data-testid="pilot-invite-copy"><Copy className="w-3.5 h-3.5 shrink-0" />{link}</button>
        </div>
      ) : <p className="text-sm text-white/45 mt-2">No account or invite.</p>}
    </Box>
  );
};

const Errors = ({ items, reload }) => {
  const resolve = async (id) => { await api.post(`/admin/errors/${id}/resolve`); reload(); };
  return (
    <Box title={`Open issues (${items.length})`} testid="monitor-errors">
      {!items.length && <p className="text-sm text-white/45">No open issues.</p>}
      <ul className="space-y-2 max-h-64 overflow-y-auto">
        {items.map((e) => (
          <li key={e.id} className="flex items-start gap-3 text-sm border-l-2 border-red-400 pl-3" data-testid={`monitor-error-${e.id}`}>
            <div className="flex-1"><p className="font-mono text-[10px] text-white/45">{e.source} · {e.path} · {ago(e.created_at)}</p><p className="text-white/80">{e.message}</p></div>
            <button onClick={() => resolve(e.id)} className="font-mono text-[10px] text-brand" data-testid={`monitor-resolve-${e.id}`}>RESOLVE</button>
          </li>
        ))}
      </ul>
    </Box>
  );
};

const Feed = ({ title, rows, render, testid }) => (
  <Box title={title} testid={testid}>
    <ul className="divide-y divide-divider max-h-80 overflow-y-auto text-sm">{rows.map(render)}</ul>
    {!rows.length && <p className="text-sm text-white/45">Nothing yet.</p>}
  </Box>
);

const Md = ({ text }) => (
  <div className="space-y-2 text-sm text-white/80" data-testid="case-study-narrative">
    {text.split("\n").map((l, i) => {
      const t = l.trim();
      if (!t) return null;
      if (t.startsWith("#")) return <p key={i} className="font-bold text-white text-base pt-3">{t.replace(/^#+\s*/, "")}</p>;
      if (/^[-*]\s/.test(t)) return <p key={i} className="pl-4">• {t.slice(2).replace(/\*\*/g, "")}</p>;
      return <p key={i}>{t.replace(/\*\*/g, "")}</p>;
    })}
  </div>
);

const CaseStudy = () => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/admin/case-study").then((r) => setD(r.data)), []);
  useEffect(() => { load(); }, [load]);
  const gen = async () => { setBusy(true); try { await api.post("/admin/case-study/generate"); await load(); toast.success("MARI updated the case study"); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const pdf = async () => { const r = await api.get("/admin/case-study/pdf", { responseType: "blob" }); const a = document.createElement("a"); a.href = URL.createObjectURL(r.data); a.download = "aegis-pilot-case-study.pdf"; a.click(); };
  if (!d) return null;
  const m = d.metrics;
  const tiles = [["Sessions", m.usage.sessions], ["Whop payments", m.payments.count], ["Verified", m.verifications.completed], ["Completion", `${m.verifications.completion_rate_pct}%`],
    ["Disputes", m.disputes.count], ["Won", m.disputes.won], ["Recovered", money(m.disputes.recovered)], ["Hours saved (est.)", m.time_saved_hours_est]];
  return (
    <Box title="Pilot case study · written by MARI from real data only" testid="monitor-case-study" className="lg:col-span-3">
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
        {tiles.map(([k, v]) => <div key={k} data-testid={`case-tile-${k.toLowerCase().replace(/[^a-z]/g, "")}`}><p className="label-xs">{k}</p><p className="font-mono text-xl font-extrabold text-brand">{v}</p></div>)}
      </div>
      <div className="flex flex-wrap gap-2 mt-4">
        <NeonButton onClick={gen} disabled={busy} data-testid="case-generate-btn"><Sparkles className="w-3 h-3" /> {busy ? "MARI is writing…" : d.latest ? "Regenerate with latest data" : "Generate case study"}</NeonButton>
        {d.latest && <NeonButton variant="ghost" onClick={pdf} data-testid="case-pdf-btn"><FileDown className="w-3 h-3" /> PDF</NeonButton>}
      </div>
      {d.latest ? <div className="mt-5"><p className="font-mono text-[10px] text-white/40">generated {ago(d.latest.generated_at)}</p><Md text={d.latest.narrative} /></div>
        : <p className="text-sm text-white/45 mt-4">Not generated yet. It's best after Brennen has used AEGIS for a few days.</p>}
    </Box>
  );
};

const TABS = [["insights", "Merchant insights"], ["errors", "Error Center"], ["console", "Guard console"], ["security", "Security"], ["ops", "Operations & health"]];

const ConsoleTab = () => {
  const { st } = useGuard();
  return <section className="panel p-6" data-testid="monitor-console"><p className="label-xs mb-4">Talk to Guard about errors, health, fraud or anything on the platform · tap the orb to speak</p><GuardConsole level={st?.level} session="audit" big testid="audit-console" /></section>;
};

const Ops = ({ d, load }) => (
  <div className="grid lg:grid-cols-3 gap-6">
    <Health h={d.health} />
    <Pilot p={d.pilot} />
    <Errors items={d.errors} reload={load} />
    <Feed title="Whop events" testid="monitor-whop" rows={d.whop_events} render={(e) => (
      <li key={e.webhook_id} className="py-2 flex gap-3"><span className={`font-mono text-[10px] w-16 ${e.status === "failed" ? "text-red-400" : "text-brand"}`}>{e.status}</span><span>{e.type}</span><span className="text-white/40 text-xs ml-auto">{ago(e.received_at)}</span></li>)} />
    <Feed title="Verification sessions" testid="monitor-verifications" rows={d.verifications} render={(v) => (
      <li key={v.id} className="py-2 flex gap-3"><span className="font-mono text-[10px] w-20 uppercase">{v.status}</span><span className="truncate">{v.email || v.name || v.id}</span><span className="text-white/40 text-xs ml-auto">{ago(v.completed_at || v.started_at)}</span></li>)} />
    <Feed title="Team" testid="monitor-users" rows={d.users} render={(u) => (
      <li key={u.id} className="py-2 flex gap-3"><span className="truncate flex-1">{u.name} <span className="text-white/45">{u.email}</span></span><span className="font-mono text-[10px] text-brand uppercase">{u.is_platform_admin ? "admin" : u.role}</span><span className="text-white/40 text-xs">{ago(u.last_seen)}</span></li>)} />
    <Feed title="Audit trail (all users)" testid="monitor-audit" rows={d.audit} render={(a, i) => (
      <li key={i} className="py-2 flex gap-3"><span className="truncate flex-1">{a.action} <span className="text-white/45">{a.entity_id}</span></span><span className="text-white/40 text-xs">{a.actor}</span></li>)} />
    <CaseStudy />
  </div>
);

export default function AdminMonitor() {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("insights");
  const [causes, setCauses] = useState(null);
  const load = useCallback(() => { api.get("/admin/overview").then((r) => setD(r.data)); api.get("/admin/errors/groups").then((r) => setCauses(r.data.kinds)).catch(() => {}); }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  const h = d?.health;
  const sys = h ? [h.api, h.database, h.storage, h.whop.webhook_secret, h.whop.api_key] : [];
  return (
    <div data-testid="admin-monitor">
      <PageHeader eyebrow="// platform admin · only you can see this" title="Admin Monitor">
        {h && <span className={`font-mono text-[10px] px-2 py-1 border ${sys.every(Boolean) ? "border-brand/40 text-brand" : "border-red-400/50 text-red-300"}`} data-testid="monitor-sys-badge">
          SYSTEMS {sys.filter(Boolean).length}/{sys.length} OK{causes ? ` · ${causes} OPEN ERROR CAUSE${causes > 1 ? "S" : ""}` : ""}</span>}
        <NeonButton variant="ghost" onClick={load} data-testid="monitor-refresh"><RefreshCw className="w-3 h-3" /> Refresh</NeonButton>
      </PageHeader>
      <div className="flex gap-1 mb-6 border-b border-white/10" data-testid="monitor-tabs">
        {TABS.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} className={`px-4 py-2.5 font-mono text-[11px] tracking-widest uppercase border-b-2 -mb-px transition-colors ${tab === k ? "border-brand text-brand" : "border-transparent text-white/50 hover:text-white"}`} data-testid={`monitor-tab-${k}`}>{label}</button>
        ))}
      </div>
      <div className={tab === "insights" ? "" : "hidden"}><Insights /></div>
      {tab === "errors" && <ErrorCenter />}
      {tab === "console" && <ConsoleTab />}
      {tab === "security" && <SecurityBody />}
      {tab === "ops" && (d ? <Ops d={d} load={load} /> : <p className="font-mono text-xs text-white/40">loading monitor…</p>)}
    </div>
  );
}
